import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';
import { inviteAdmin } from '../../../store/actions/userActions';
import { BmModal, Btn, Field, Input, Badge } from '../../ForBrands/ui/BrandUI';
import { Icon } from '../../ForBrands/ui/icons';

// Pulls the JSON error body out of secureFetch's "HTTP NNN: {body}" message.
const parseFetchError = (err) => {
  const m = /HTTP \d+: (.+)$/s.exec(err?.message || '');
  if (!m) return null;
  try { return JSON.parse(m[1]); } catch { return { error: m[1] }; }
};

const InviteAdminDialog = ({ open, onClose, onInvited }) => {
  const dispatch = useDispatch();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) return null;

  const reset = () => {
    setEmail('');
    setFirstName('');
    setLastName('');
    setSubmitting(false);
    setResult(null);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      const res = await dispatch(inviteAdmin({
        email: trimmed,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      }));
      setResult(res);
      toast.success(res?.emailSent
        ? `Invite emailed to ${res.email}.`
        : `Admin created for ${res.email}.`);
      if (onInvited) onInvited(res);
    } catch (err) {
      const parsed = parseFetchError(err);
      toast.error(parsed?.error || 'Could not send invite.');
    } finally {
      setSubmitting(false);
    }
  };

  const copyPassword = () => {
    if (!result?.tempPassword) return;
    navigator.clipboard?.writeText(result.tempPassword).then(
      () => toast.success('Temp password copied.'),
      () => toast.error('Could not copy. Select and copy manually.'),
    );
  };

  // A temporary password is shown once, so while it is on screen only Done
  // and the X close the dialog; Escape and the backdrop do not.
  return (
    <BmModal
      title="Invite admin"
      onClose={handleClose}
      width={480}
      dismissible={!result?.tempPassword}
      footer={!result ? (
        <>
          <Btn variant="secondary" onClick={handleClose} disabled={submitting}>Cancel</Btn>
          <Btn onClick={handleSubmit} disabled={submitting || !email.trim()}>{submitting ? 'Sending...' : 'Send invite'}</Btn>
        </>
      ) : (
        <>
          <Btn variant="secondary" onClick={reset}>Invite another</Btn>
          <Btn onClick={handleClose}>Done</Btn>
        </>
      )}
    >
      {!result ? (
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="bm-body-s text-bm-muted">
            The new admin receives an email with a temporary password and sets their own on first sign-in.
          </p>
          <Field label="Email">
            <Input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="new.admin@example.com"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name" hint="Optional">
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </Field>
            <Field label="Last name" hint="Optional">
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </Field>
          </div>
        </form>
      ) : (
        <div className="space-y-3">
          <Badge tone={result.emailSent ? 'outline' : 'attention'} dot={result.emailSent ? '#1f9d55' : undefined}>
            {result.emailSent ? `Invitation emailed to ${result.email}` : `Admin created for ${result.email}, email failed`}
          </Badge>
          {!result.emailSent && (
            <p className="bm-body-s text-bm-muted">Share the temporary password manually.</p>
          )}
          {result.tempPassword && (
            <>
              <p className="bm-body-s text-bm-muted">Temporary password, shown once. The invitee sets their own on first sign-in.</p>
              <div className="flex items-center gap-2 rounded-[8px] border border-bm-border bg-bm-subtle px-3 py-2">
                <code className="flex-1 select-all truncate font-mono bm-body-m text-bm-ink">{result.tempPassword}</code>
                <Btn size="sm" variant="secondary" onClick={copyPassword}>
                  <Icon name="copy" size={12} className="mr-1" /> Copy
                </Btn>
              </div>
            </>
          )}
        </div>
      )}
    </BmModal>
  );
};

export default InviteAdminDialog;
